/**
 * Copyright © Binary Anvil, Inc. All rights reserved.
 * See https://binaryanvil.com/software/license for license details.
 */
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import React from "react";
import LayoutFullWidth from "@/app/components/layouts/LayoutFullWidth";
import {
    STATIC_DATA_REVALIDATE_PERIOD_SECONDS
} from "@/app/constants";
import {getCommonStaticProps} from "@/app/utils/getStaticProps/getCommonStaticProps";
import {TCommonStaticDataObject} from "@/app/types/prefetchedData/commonStaticData";
import getMaintenanceMode from "@/app/utils/getMaintenanceMode";
import {GetServerSideProps, GetStaticPaths} from "next";
import {TBrandsFullData} from "@/app/types/brands/brandTypes";
import {useTranslation} from "next-i18next";
import {getBrandDetailedStaticProps} from "@/app/utils/getStaticProps/getBrandDetailedStaticProps";
import BrandDetailsView from "@/app/components/brands/BrandDetailsView";
import {getBrandSitemapItems} from "@/app/utils/getBrandSitemapItems";
import {BRAND_DETAILS_PAGE_PREFIX} from "@/app/config/brandsConfig";

export const getStaticPaths: GetStaticPaths = async () => {
    // const brandsItems = await getBrandSitemapItems();
    // Locations pages will be generated on demand. Uncomment logic to pregenerate brands pages
    return {paths: [
            // ...brandsItems.brandsRelativeUrls.filter((item) => item.includes(`/${BRAND_DETAILS_PAGE_PREFIX}/`)),
        ], fallback: 'blocking'}
}

export const getStaticProps: GetServerSideProps = async (context) => {
    const slug = (context.params?.slug as string);
    let pageFound = false;
    let brandPageData: TBrandsFullData | null = null;
    const brandRequestData = await getBrandDetailedStaticProps({brandSlug: slug});

    if (brandRequestData && !brandRequestData.isNetworkError) {
        if (brandRequestData.brands.length > 0 && brandRequestData.brands[0]) {
            pageFound = true;
            brandPageData = brandRequestData;
        }
    }

    return {
        props: {
            ...(await serverSideTranslations(context.locale ?? '', ['common'])),
            maintenanceMode: await getMaintenanceMode(),
            commonStaticData: await getCommonStaticProps(),
            brandPageData: brandPageData,
        },
        notFound: !pageFound,
        revalidate: STATIC_DATA_REVALIDATE_PERIOD_SECONDS
    }
}

export default function BrandsListPage(props: {
    brandPageData: TBrandsFullData,
    maintenanceMode?: boolean,
    commonStaticData: TCommonStaticDataObject
}) {
    const {t} = useTranslation();
    if (props.brandPageData.brands && props.brandPageData.brands.length > 0 && props.brandPageData.brands[0]) {
        return <BrandDetailsView brand={props.brandPageData.brands[0]}/>
    }

    return <LayoutFullWidth
        headTitle={t('No data for chosen brand')}
        metaDescription={t('Discover a list of available brands')}
        mainContainerClassName={`brands-list-page`}
    >
        <p>{t('No data for chosen brand')}</p>
    </LayoutFullWidth>
}